import React from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import Layout from '../../components/Layout';
import { queueBulkEmail } from '../../services/bulkEmail';
import { showError, showSuccess } from '../../services/toaster';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BulkEmail = () => {
    const [emailInput, setEmailInput] = React.useState('');
    const [subject, setSubject] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);

    const parsedEmails = React.useMemo(
        () => Array.from(new Set(
            emailInput.split(',').map((email) => email.trim().toLowerCase()).filter(Boolean)
        )),
        [emailInput]
    );
    const invalidEmails = parsedEmails.filter((email) => !EMAIL_PATTERN.test(email));

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!parsedEmails.length) return showError('Enter at least one email address');
        if (parsedEmails.length > 500) return showError('A maximum of 500 email addresses is allowed');
        if (invalidEmails.length) return showError('Correct the invalid email addresses before sending');
        if (!subject.trim() || !message.trim()) return showError('Subject and message are required');

        setSubmitting(true);
        const response = await queueBulkEmail({
            emails: parsedEmails,
            subject: subject.trim(),
            message: message.trim(),
        });
        setSubmitting(false);

        if (response?.error || response?.status >= 400) {
            showError(response?.message || 'Unable to queue emails');
            return;
        }

        showSuccess(response?.data?.message || `${parsedEmails.length} emails queued successfully`);
        setEmailInput('');
        setSubject('');
        setMessage('');
    };

    return (
        <Layout appBarTitle="Bulk Email">
            <Paper component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h5" gutterBottom>Send bulk email</Typography>
                    </Box>

                    <TextField
                        label="Email addresses"
                        placeholder="person1@example.com, person2@example.com"
                        value={emailInput}
                        onChange={(event) => setEmailInput(event.target.value)}
                        multiline
                        minRows={8}
                        fullWidth
                        required
                        error={invalidEmails.length > 0 || parsedEmails.length > 500}
                        helperText={
                            invalidEmails.length
                                ? `Invalid: ${invalidEmails.slice(0, 3).join(', ')}`
                                : `${parsedEmails.length} unique recipient${parsedEmails.length === 1 ? '' : 's'} (maximum 500)`
                        }
                    />

                    <TextField
                        label="Subject"
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        inputProps={{ maxLength: 200 }}
                        fullWidth
                        required
                    />

                    <TextField
                        label="Message"
                        value={message}
                        onChange={(event) => setMessage(event.target.value)}
                        multiline
                        minRows={10}
                        fullWidth
                        required
                    />

                    <Box>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SendIcon />}
                            disabled={submitting}
                            size="large"
                        >
                            {submitting ? 'Sending…' : 'Send Email'}
                        </Button>
                    </Box>
                </Stack>
            </Paper>
        </Layout>
    );
};

export default BulkEmail;
